import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box, Stack, Typography, TextField, IconButton, Button, Divider, Link,
} from '@mui/material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SectionCard from '../ui/SectionCard';
import * as actions from '../../../../redux/actions';

// ลิงก์ Google Drive ของ opportunity ใบนี้ — แบ่ง 2 ส่วนตามที่ user ขอ:
// 1) จาก LINE OA (google_drive_url) — แอปนอกเขียนเข้ามาทาง backend/routes/external.js เท่านั้น
//    (ดู backend/services/external.service.js) พนักงาน "อ่านอย่างเดียว" ในหน้านี้ ไม่มีช่องแก้ไข/
//    ลบให้เลย ตั้งใจไม่ส่ง google_drive_url ผ่าน updateOpportunity อีกต่อไป (บล็อกฝั่ง backend
//    ด้วย ดู opportunity.repository.js — ไม่ใช่แค่ซ่อนปุ่มฝั่ง UI เฉยๆ)
// 2) เพิ่มเอง (google_drive_links) — array เพิ่ม/ลบได้ไม่จำกัดจำนวน เก็บทีเดียวทั้งก้อนตอนกด
//    "บันทึก" (pattern เดียวกับ documents[] ของ Project ใน ProjectEditPopup.js)
export function GoogleDriveCard({
  opportunity, onChanged, onLocalUpdate, showSnack,
}) {
  const dispatch = useDispatch();
  const lineOaUrl = opportunity.google_drive_url;

  const [links, setLinks] = useState(opportunity.google_drive_links || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLinks(opportunity.google_drive_links || []);
  }, [opportunity.google_drive_links]);

  const addLink = () => setLinks((prev) => [...prev, { label: '', url: '' }]);
  const updateLink = (idx, field, v) => setLinks((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: v } : l)));
  const removeLink = (idx) => setLinks((prev) => prev.filter((_, i) => i !== idx));

  const handleSaveLinks = async () => {
    const cleaned = links.filter((l) => (l.url || '').trim());
    setSaving(true);
    onLocalUpdate?.({ google_drive_links: cleaned });
    try {
      await dispatch(actions.updateOpportunity({ id: opportunity.id, googleDriveLinks: cleaned }));
      onChanged();
    } catch (err) {
      showSnack(err.message || 'บันทึกไม่สำเร็จ', 'error');
      onChanged();
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard icon={<FolderOutlinedIcon />} title="ลิงก์ Google Drive" accentColor="info.main">
      <Stack spacing={2}>
        {/* ส่วนที่ 1: จาก LINE OA — read-only */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
            <LockOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
            <Typography variant="caption" fontWeight={700} color="text.secondary">
              จาก LINE OA (แก้ไข/ลบเองไม่ได้)
            </Typography>
          </Stack>
          {lineOaUrl ? (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Link
                href={lineOaUrl}
                target="_blank"
                rel="noopener"
                sx={{ fontSize: '0.8rem', wordBreak: 'break-all' }}
              >
                {lineOaUrl}
              </Link>
              <IconButton size="small" onClick={() => window.open(lineOaUrl, '_blank')}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.disabled">ยังไม่มีลิงก์จาก LINE OA</Typography>
          )}
        </Box>

        <Divider />

        {/* ส่วนที่ 2: พนักงานเพิ่มเอง — เพิ่ม/ลบได้ไม่จำกัด */}
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary">เพิ่มเอง</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addLink}>เพิ่มลิงก์</Button>
          </Stack>
          <Stack spacing={1}>
            {links.map((link, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <Stack key={idx} direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  placeholder="ชื่อ"
                  value={link.label}
                  onChange={(e) => updateLink(idx, 'label', e.target.value)}
                  sx={{ width: 140, flexShrink: 0 }}
                />
                <TextField
                  size="small"
                  placeholder="URL"
                  value={link.url}
                  onChange={(e) => updateLink(idx, 'url', e.target.value)}
                  fullWidth
                />
                <IconButton
                  size="small"
                  disabled={!link.url?.trim()}
                  onClick={() => window.open(link.url, '_blank')}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => removeLink(idx)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            {links.length === 0 && (
              <Typography variant="caption" color="text.secondary">ยังไม่มีลิงก์ที่เพิ่มเอง</Typography>
            )}
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveLinks}
              disabled={saving}
            >
              บันทึก
            </Button>
          </Box>
        </Box>
      </Stack>
    </SectionCard>
  );
}

export default GoogleDriveCard;
